import { Dispatch, FC, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Badge, type BadgeProps, Button, Flex, Modal, type ModalProps, Spin, Typography } from 'antd';

import CalendarIcon from 'assets/images/icons/calendar-with-clock.svg?react';
import EyeIcon from 'assets/images/icons/eye.svg?react';
import { usePlannerPostsPublicationsListQuery } from 'features/Planner';
import { EMainRoutes, EYmPageScreen } from 'shared/typings';
import { ButtonStyleLink } from 'shared/ui';
import { sendYmEvent } from 'shared/utils';

interface ISuccessPopupProps extends ModalProps {
	postType: 'instant' | 'regular';
	publishedAt: string | undefined;
	setIsSuccessModalOpen: Dispatch<SetStateAction<boolean>>;
	isEdit: boolean;
	postId?: string;
}

export const SuccessPopup: FC<ISuccessPopupProps> = ({
	postType = 'instant',
	publishedAt,
	setIsSuccessModalOpen,
	isEdit,
	postId,
	...props
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data: publications } = usePlannerPostsPublicationsListQuery(
		{ plannedPostId: postId || '' },
		{ skip: !postId, pollingInterval: 1000 },
	);

	const channelLink = publications?.[0]?.channel_link;
	const postLink = publications?.[0]?.published_post?.post_link;

	const contentMap = {
		instant: {
			title: !postLink ? t('Пост публикуется...') : t('Пост опубликован'),
			text: !postLink ? t('Публикация будет учтена в планнере') : t('Публикация была учтена в планнере'),
			btnText: !postLink ? t('Перейти в канал') : t('Показать в канале'),
			btnIcon: <EyeIcon />,
			status: (!postLink ? 'processing' : 'success') as BadgeProps['status'],
			href: postLink || channelLink || '',
			ymEvent: null,
		},
		regular: {
			title: isEdit ? t('Пост сохранен') : t('Пост запланирован'),
			text: t('Отредактировать или отменить публикацию можно в планнере'),
			btnText: t('Показать в планнере'),
			btnIcon: <CalendarIcon />,
			status: 'processing' as BadgeProps['status'],
			goTo: `/${EMainRoutes.PLANNER}?plannerDate=${publishedAt}&plannerType=day`,
			ymEvent: 'click_show_in_scheduler',
		},
	};

	const content = isEdit ? contentMap.regular : contentMap[postType];

	const handleCloseClick = () => {
		setIsSuccessModalOpen(false);
		navigate(-1);
	};

	const handleGoToClick = () => {
		content.ymEvent && sendYmEvent(content.ymEvent, { page_screen: EYmPageScreen.POPUP });
		setIsSuccessModalOpen(false);
		if (postType === 'instant') {
			navigate(`/${EMainRoutes.PLANNER}?plannerDate=${publishedAt}&plannerType=day`);
		}
	};

	return (
		<Modal
			title={
				<Flex align="center" gap={8}>
					{!postLink && postType === 'instant' && !isEdit ? <Spin size="small" /> : <Badge status={content.status} />}{' '}
					{content.title}
				</Flex>
			}
			destroyOnHidden
			footer={
				<Flex gap={8} justify="space-between">
					{'goTo' in content && (postType === 'regular' || isEdit) && (
						<ButtonStyleLink
							to={content.goTo}
							type="primary"
							size="large"
							icon={content.btnIcon}
							htmlType="button"
							onClick={handleGoToClick}>
							{content.btnText}
						</ButtonStyleLink>
					)}

					{'href' in content && postType === 'instant' && (
						<Button
							href={content.href}
							type="primary"
							size="large"
							icon={content.btnIcon}
							htmlType="button"
							onClick={handleGoToClick}
							disabled={!postLink && !channelLink}
							loading={!postLink && !channelLink}>
							{content.btnText}
						</Button>
					)}

					<Button size="large" htmlType="button" onClick={handleCloseClick}>
						{t('Закрыть')}
					</Button>
				</Flex>
			}
			onCancel={handleCloseClick}
			{...props}>
			<Typography.Paragraph>{content.text}</Typography.Paragraph>
		</Modal>
	);
};
